import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GitBranch, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export const ProjectionRevision = ({ projection, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const start = () => {
    setMonths((projection.forecast_data?.organization_forecast?.months || []).map(m => ({ month: m.month, predicted_leads: m.predicted_leads, predicted_closures: m.predicted_closures })));
    setReason(''); setError(''); setOpen(true);
  };
  const update = (index, key, value) => setMonths(previous => previous.map((m, i) => i === index ? { ...m, [key]: value === '' ? '' : Number(value) } : m));
  const save = async () => {
    if (!reason.trim() || months.some(m => ['predicted_leads', 'predicted_closures'].some(k => !Number.isInteger(m[k]) || m[k] < 0))) {
      setError('Enter a reason and nonnegative whole counts for every month.'); return;
    }
    setSaving(true); setError('');
    try {
      const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/forecast-enhanced/update-projection/${projection.projection_id}`, {
        expected_version: projection.version || 1, reason, changes: { monthly_predictions: months },
      }, { withCredentials: true });
      toast.success(`New version created: ${data.projection_id}`); setOpen(false); onCreated?.();
    } catch (err) { setError(err.response?.data?.detail || 'Could not create a version. Original predictions are unchanged.'); }
    finally { setSaving(false); }
  };
  return <>
    <Button variant="outline" data-testid={`revise-${projection.projection_id}`} onClick={start}><GitBranch className="h-4 w-4 mr-2" />Create adjusted version</Button>
    <Dialog open={open} onOpenChange={value => !saving && setOpen(value)}>
      <DialogContent data-testid="projection-revision-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New prediction version</DialogTitle><DialogDescription data-testid="revision-preservation">Original predictions stay unchanged. Dealer/product allocations use saved shares; adjustments are not backtested.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {months.map((m, i) => <div key={m.month} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end" data-testid={`revision-row-${m.month}`}>
            <span className="font-medium">{m.month}</span>
            <label className="text-xs">Enquiries<Input data-testid={`revision-leads-${m.month}`} type="number" min="0" step="1" value={m.predicted_leads} onChange={e => update(i, 'predicted_leads', e.target.value)} /></label>
            <label className="text-xs">Net orders<Input data-testid={`revision-orders-${m.month}`} type="number" min="0" step="1" value={m.predicted_closures} onChange={e => update(i, 'predicted_closures', e.target.value)} /></label>
          </div>)}
          <label className="block text-sm">Reason<Input data-testid="revision-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={2000} /></label>
          {error && <p role="alert" data-testid="revision-error" className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end"><Button variant="outline" data-testid="revision-cancel" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="revision-save" disabled={saving || !reason.trim()} onClick={save}>{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save new version</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  </>;
};